/* TODO: t() */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button, ButtonToolbar, Form, Picker, SubmitButton } from '../uiold';
import LoadingIndicator from '../uiold/LoadingIndicator';
import { showSuccess } from '../App/AppAlert';
import { useFetchJson } from '../../support/useFetch';
import formatDate from '../../core/support/formatDate';

import { EditorStateProvider } from './EditorContext';
import { FormElementList } from './FormElementList';

import { EvaluationFormDefinition } from '../../types/shared/EvaluationForm';
import {
  FormSubmissionByMarket,
  SubmissionRequest,
  SubmissionField,
} from '../../types/shared/FormSubmission';
import { Market } from '../../types/shared/Market';

type Props = {
  markets: Array<Market>;
  submissions: FormSubmissionByMarket;
  revalidateSubmissions: () => void;
};

function FormEditor(props: Props) {
  let { markets, submissions, revalidateSubmissions } = props;
  let fetchJson = useFetchJson();

  let { handleSubmit, register, reset } = useForm();
  let [isEditing, setIsEditing] = useState(false);

  let openEdit = () => {
    setIsEditing(true);
  };
  let closeEdit = () => {
    setIsEditing(false);
  };

  let mapper = useCallback(
    (date: string, market: Market) => ({
      date,
      name: `${formatDate(date, { fromUTC: true, includeDayOfWeek: true })}${
        submissions[market.id]?.[date] ? ' (Filled)' : ''
      }`,
    }),
    [submissions],
  );

  let {
    selectedMarket,
    selectedMarketDates,
    setSelectedMarket,
    selectedDate,
    setSelectedDate,
  } = useMarketAndDatePicker(markets, { mapper });

  let submission =
    submissions[selectedMarket?.id ?? '']?.[selectedDate?.date ?? ''];
  let hasSubmission = !!submission;

  let { data: formDef } = useQuery(
    ['forms/evaluation', submission?.revision],
    () => {
      const url = `/api/forms/evaluation${
        submission ? '/' + submission.revision : ''
      }`;
      return fetchJson<EvaluationFormDefinition>(url);
    },
  );

  const { mutate: submit, isLoading: isSubmitting } = useMutation(
    ['forms/evaluation/submissions'],
    async (input: ObjectOf<SubmissionField>) => {
      if (!formDef || !selectedDate || !selectedMarket) {
        return;
      }
      const fields = [];
      for (const { id } of formDef.fields) {
        const submissionField = input[id];
        submissionField && fields.push(submissionField);
      }
      const body: SubmissionRequest = {
        // The id will be generated by the BE
        id: '',
        marketID: selectedMarket.id,
        date: selectedDate.date,
        revision: formDef.revision,
        fields,
      };
      const data = await fetchJson('/api/forms/evaluation/submissions', {
        body,
      });
      if (data.success === true) {
        reset();
        revalidateSubmissions();
        showSuccess('Submitted Successfully.');
      }
    },
  );

  const preventSubmit =
    !formDef ||
    isSubmitting ||
    hasSubmission ||
    !selectedDate ||
    !selectedMarket ||
    formDef.fields.length === 0;

  return (
    <Form onSubmit={handleSubmit((input) => submit(input))} style={styles.form}>
      <ButtonToolbar style={styles.toolbar}>
        <Picker
          options={markets}
          selectedItem={selectedMarket}
          onChange={(market) => {
            setSelectedMarket(market);
            closeEdit();
          }}
          style={{ marginRight: 5 }}
        />
        <Picker
          options={selectedMarketDates}
          selectedItem={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            closeEdit();
          }}
          style={{ marginRight: 5 }}
        />
        <View style={styles.submitSpacing} />
        {/* TODO: Fix editing submissions later and enable this */}
        {false && hasSubmission ? (
          isEditing ? (
            <Button onPress={closeEdit} title="Cancel" style={styles.button} />
          ) : (
            <Button onPress={openEdit} title="Edit" style={styles.button} />
          )
        ) : null}
        {(!preventSubmit || isEditing) && (
          <SubmitButton onPress={closeEdit} title="Submit" />
        )}
      </ButtonToolbar>
      <EditorStateProvider value={{ register, submission }}>
        {formDef ? (
          <FormElementList
            {...props}
            formDef={formDef}
            mode={isEditing ? 'edit' : hasSubmission ? 'readOnly' : 'edit'}
          />
        ) : (
          <LoadingIndicator />
        )}
      </EditorStateProvider>
    </Form>
  );
}

type NamedDate = { date: string; name: string };

// TODO: Move to helpers/
function useMarketAndDatePicker(
  markets: Array<Market>,
  options: {
    mapper?: (date: string, market: Market) => NamedDate;
    filter?: (date: string, market: Market) => boolean;
  } = {},
) {
  let { mapper, filter } = options;
  let [selectedMarket, setSelectedMarket] = useState(markets[0]);
  let [selectedDate, setSelectedDate] = useState<NamedDate | undefined>();

  useEffect(() => {
    // Reset the state if selectedMarket is not part of available markets.
    // This might happen if the markets has changed because of changing account,
    // but the selectedMarket is still from the previous account.
    if (!selectedMarket || !markets.includes(selectedMarket)) {
      setSelectedMarket(markets[0]);
    }
  }, [markets, selectedMarket]);

  let selectedMarketDates = useMemo(() => {
    if (!selectedMarket) {
      return [];
    }
    let market = selectedMarket;

    let marketDates = market.dates
      .filter(filter ? (date) => filter?.(date, market) : () => true)
      .map(
        mapper
          ? (date) => mapper?.(date, market)
          : (date) => ({
              date,
              name: formatDate(date, { fromUTC: true, includeDayOfWeek: true }),
            }),
      );

    let [firstDate] = marketDates;

    setSelectedDate(firstDate);

    return marketDates;
  }, [selectedMarket, mapper, filter]);

  return {
    selectedMarket,
    selectedMarketDates,
    setSelectedMarket,
    selectedDate,
    setSelectedDate,
  };
}

let styles = StyleSheet.create({
  form: {
    flex: 1,
    maxWidth: 768,
  },
  toolbar: {
    zIndex: 1,
    marginBottom: 8,
  },
  submitSpacing: {
    flex: 1,
  },
  button: {
    paddingHorizontal: 10,
    marginHorizontal: 10,
  },
});

export default FormEditor;
